import { useEffect, useRef } from 'react'

import { setOptimizelyExperiment } from 'actions/OptimizelyActions'
import { optimizelyIsReady, userContext } from 'analytics/optimizely/optimizely'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import createSelector from 'lib/web/createSelector'
import { OptimizelyDecideOption } from '@optimizely/optimizely-sdk'
import { reportClientError } from 'services/errorReportingService'

export type OptimizelyExperiments = OptimizelyClientSideExperiments | OptimizelyServerSideExperiments;

export enum OptimizelyClientSideExperiments {
  lereLpcLowRpvFooterEnabled = 'lere_lpc_sticky_footer_low_rpv',
  searchPriceMapMarkers = 'search_price_map_markers',
  searchNightlyPriceMapMarkers = 'search_nightly_price_map_markers_discount',
  searchFallbackEvToAu = 'search_fallback_to_au_ev_test_ab',
  searchAuStateSegmentRanking = 'search_au_state_segment_ranking',
  searchTypeaheadOpenSearch = 'search_typeahead_opensearch',
  isTourReviewsEnabled = 'cro_tours_reviews',
  paymentsMerchantFees = 'payments_merchant_fees',
  paymentsFlightsMerchantFees = 'payments_flight_merchant_fees',
  signUpOfferDetails = 'cro_offer_details_sign_up_modal',
  paymentsBookingProtection = 'payments_booking_protection',
  searchBedbankAmenities = 'search_bedbank_amenities',
  paymentsBookingProtectionDomestic = 'payments_booking_protection_domestic',
}

export enum OptimizelyServerSideExperiments {
  isSupportAssistantEnabled = 'opex_support_assistant_customer_pool',
  aaExperimentEnabled = 'aa_experiment_23_01',
  isPersonalisedRecCalendar = 'lere_personalised_recos_in_calendar_with_offer_id',
  paymentsPayToRelease = 'payments_payto_release',
  offerImageGrid = 'cro_offer_page_image_gallery',
  newHomepageWithSignupDelay = 'cro_new_homepage_with_signup_delay',
  lereOfferPageEmbeddedCarousel = 'lere_embedded_carousel',
  hotelSearchPageBedbankLos = 'hotel_search_page_bedbank_los',
}

export async function activateOptimizelyExperiment(flagKey: OptimizelyClientSideExperiments | OptimizelyServerSideExperiments) {
  // This function shouldn't be called on server
  if (typeof window === 'undefined') {
    return
  }

  const success = await optimizelyIsReady

  if (success) {
    try {
      const decision = userContext.decideForKeys([flagKey])[flagKey]
      const experimentValues = {
        variationKey: decision.variationKey,
        variables: decision.variables,
        enabled: decision.enabled,
      }

      return experimentValues
    } catch (e) {
      reportClientError(e, { flagKey }) }
  }
}

/**
 * Optimizely Experiments Selectors.  Each experiment can be found in Redux in state.optimizely.optimizelyExperiments.
 * Each has an 'enabled' value that can be used to determine if the user is enrolled in a variant (if the user is
 * part of the control, or the test isn't live in Optimizely, the value will be false).  Each experiment also has
 * a 'variationKey' value that can be used to determine which variant the user is enrolled in (think of this as
 * the same thing as the number variant previously used in Optimize).  Each experiment also has a 'variables'value
 * that can be used to pass variables from Optimizely to use in the experiment.
 *
 * Return the optimizely experiment variant dynamically based on the given trigger boolean condition.
 * If canTriggerExperiment is false the experiment will not be activated.
 * Note: once the trigger becomes true once, it will be enabled for the rest of the session until page refresh.
 *
 * @param canTriggerExperiment a boolean indicating whether the experiment can be triggered
 * @param experimentId experiment id from the Optimizely UI (Rule Key)
 * @returns Experiment variant if can be triggered
 */
export function useOptimizelyExperiment(experimentId: OptimizelyClientSideExperiments | OptimizelyServerSideExperiments, canTriggerExperiment: boolean = true) {
  const dispatch = useAppDispatch()
  const experimentActivated = useRef(false)
  useEffect(() => {
    if (canTriggerExperiment && !experimentActivated.current) {
      dispatch(setOptimizelyExperiment(experimentId))
      experimentActivated.current = true
    }
  }, [dispatch, canTriggerExperiment, experimentId])

  const experimentVariation = useAppSelector(state => getOptimizelyExperimentVariation(state, experimentId))
  if (canTriggerExperiment) { return experimentVariation }
}

export function isClientSideExperiment(experimentId: OptimizelyClientSideExperiments | OptimizelyServerSideExperiments): experimentId is OptimizelyClientSideExperiments {
  return Object.values(OptimizelyClientSideExperiments).includes(experimentId as OptimizelyClientSideExperiments)
}

export const getOptimizelyExperimentVariation = createSelector(
  (state: App.State, experimentId: OptimizelyClientSideExperiments | OptimizelyServerSideExperiments) => state.optimizely.optimizelyExperiments[experimentId],
  (experiment) => {
    if (!experiment?.enabled) {
      return
    }
    if (experiment.enabled && Object.keys(experiment.variables).length) {
      return experiment.variables[experiment.variationKey]
    }
    return experiment.variationKey
  },
)

/**
 *
 *  Gets all Optimizely experiments and returns an object required for Snowplow experiment context
 */
export function allOptimizelyExperiments() {
  const allExperiments = userContext.decideAll([OptimizelyDecideOption.DISABLE_DECISION_EVENT])
  return {
    flags: Object.entries(allExperiments).map(
      ([_, decisions]) => ({
        key: `exp_${decisions.flagKey}`,
        value: decisions.variationKey || '',
      }),
    ),
  }
}
